import { Button, Text, useToast, Wrap } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import React from 'react';
import { ConnectionHandler } from 'utils/graphClient';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { groupBy } from 'utils/groupObjectsByProperty';
import { useAuthenticatedSession } from 'utils/sessionProvider';
import { notEmpty } from 'utils/NotEmptyFilter';
import { ReactionTooltip } from '../../../../../../../../components/ChatApp/components/ChatMenu/components/ChatThread/components/MessageReactions/ReactionTooltip';
import { ReactionButton } from './ReactionButton';
import { AnnouncementReactions_chat_createReaction_Mutation } from './__generated__/AnnouncementReactions_chat_createReaction_Mutation.graphql';
import { AnnouncementReactions_chat_removeReaction_Mutation } from './__generated__/AnnouncementReactions_chat_removeReaction_Mutation.graphql';
import { AnnouncementReactions_chat_SocialThreadReactionsFragment$key } from './__generated__/AnnouncementReactions_chat_SocialThreadReactionsFragment.graphql';

const socialThreadReactionsFragment = graphql`
  fragment AnnouncementReactions_chat_SocialThreadReactionsFragment on SocialThread {
    id
    reactions(first: 1000) @connection(key: "CommentsThread_reactions") {
      edges {
        node {
          id
          text
          senderId
          deletedAt
        }
      }
    }
    ...ReactionButton_chat_socialThreadFragment
  }
`;

const createReactionMutation = graphql`
  mutation AnnouncementReactions_chat_createReaction_Mutation(
    $input: CreateThreadReactionInput!
  ) {
    createThreadReaction(input: $input) {
      id
      text
      senderId
      deletedAt
      conversation {
        id
      }
    }
  }
`;

const removeReactionMutation = graphql`
  mutation AnnouncementReactions_chat_removeReaction_Mutation($id: String!) {
    removeThreadReaction(id: $id) {
      id
      text
      senderId
      deletedAt
      conversation {
        id
      }
    }
  }
`;

type Reaction = {
  id: string;
  text: unknown;
  senderId: string;
};

interface AnnouncementReactionsProps {
  socialThread: AnnouncementReactions_chat_SocialThreadReactionsFragment$key;
  announcementId: string;
}

export const AnnouncementReactions: React.FC<AnnouncementReactionsProps> = ({
  socialThread,
  announcementId,
}) => {
  const {
    id: socialThreadId,
    reactions,
    ..._socialThread
  } = useFragment(socialThreadReactionsFragment, socialThread);

  const { member: sessionMember } = useAuthenticatedSession();
  const toast = useToast();
  const onErrorToast = ({ action }: { action: 'add' | 'remove' }) =>
    toast({
      status: 'error',
      position: 'top-right',
      title:
        action === 'add'
          ? t`Could not add your reaction`
          : t`Could not remove that reaction`,
    });

  const reactionNodes = reactions.edges.map((e) => e.node);

  const [createReaction] =
    useMutation<AnnouncementReactions_chat_createReaction_Mutation>(
      createReactionMutation,
    );
  const [removeReaction] =
    useMutation<AnnouncementReactions_chat_removeReaction_Mutation>(
      removeReactionMutation,
    );

  const handleReactionClick = (
    otherReaction: Reaction,
    myReaction?: Reaction,
  ) => {
    if (myReaction) {
      removeReaction({
        variables: {
          id: myReaction.id,
        },
        payloadErrorBehaviour: 'HANDLED',
        onError: (e) => {
          onErrorToast({ action: 'remove' });
        },
        optimisticResponse: {
          removeThreadReaction: {
            id: myReaction.id,
            text: myReaction.text,
            senderId: myReaction.senderId,
            deletedAt: Date.now(),
            conversation: {
              id: socialThreadId,
            },
          },
        },
      });
      return;
    }

    const updater = (store: any) => {
      const reaction = store.getRootField('createThreadReaction');
      const thread = store.get(socialThreadId);

      if (thread) {
        const reactions = ConnectionHandler.getConnection(
          thread,
          'CommentsThread_reactions',
        );

        if (reactions) {
          const edges = reactions.getLinkedRecords('edges');
          const reactionEdge = ConnectionHandler.createEdge(
            store,
            reactions,
            reaction,
            'ReactionEdge',
          );

          const newEdges = [...(edges || []), reactionEdge].filter(notEmpty);

          reactions.setLinkedRecords(newEdges, 'edges');
        }
      }
    };

    createReaction({
      variables: {
        input: {
          referenceId: announcementId,
          reaction: otherReaction.text as string,
        },
      },
      payloadErrorBehaviour: 'HANDLED',
      onError: (e) => {
        onErrorToast({ action: 'add' });
      },
      updater,
      optimisticResponse: {
        createThreadReaction: {
          id: `optimistic-${Date.now()}`,
          text: otherReaction.text,
          senderId: sessionMember.id,
          deletedAt: null,
          conversation: {
            id: socialThreadId,
          },
          reactions: {
            edges: [],
          },
        },
      },
      optimisticUpdater: updater,
    });
  };

  const reactionsByEmoji = groupBy(reactionNodes, (n) => n.text as string);
  return (
    <Wrap spacing={1} data-qa="message-reactions" justify="end">
      {Object.entries(reactionsByEmoji).map(([emoji, groupedReactions]) => {
        const groupedReactionsWithoutDeleted = groupedReactions.filter(
          (gr) => !gr.deletedAt,
        );

        const myReaction = groupedReactionsWithoutDeleted.find(
          (r) => r.senderId === sessionMember.id,
        );

        const memberIds = groupedReactionsWithoutDeleted.map(
          (r) => r.senderId as string,
        );

        if (!groupedReactionsWithoutDeleted.length) {
          return null;
        }

        return (
          <ReactionTooltip key={emoji} memberIds={memberIds} emoji={emoji}>
            <Button
              size="xs"
              variant="solid"
              colorScheme="gray"
              px={2}
              borderWidth={1.5}
              borderColor={!!myReaction ? 'blue.200' : 'transparent'}
              borderRadius="full"
              onClick={() =>
                handleReactionClick(
                  groupedReactionsWithoutDeleted[0] as Reaction,
                  myReaction as Reaction,
                )
              }
            >
              <Text fontSize="md">{emoji}</Text>
              <Text fontSize="xs" pl={1}>
                {groupedReactionsWithoutDeleted.length}
              </Text>
            </Button>
          </ReactionTooltip>
        );
      })}
      <ReactionButton
        socialThread={_socialThread}
        announcementId={announcementId}
      />
    </Wrap>
  );
};
